import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Plus, Search, X } from "lucide-react";
import {
  DsBadge,
  DsButton,
  DsIcon,
  DsInput,
} from "../../../../../design-system";
import InfoHint from "./InfoHint";
import {
  collectHolidayYears,
  formatHolidayTypeLabel,
  holidayTypeTone,
  holidayYear,
} from "../../../../../lib/holidayBankUtils";

/**
 * Separate popup: Holiday Bank as a year-wise table with Assign checkbox + ⋮ CRUD.
 */
export default function HolidayBankAssignModal({
  open,
  darkMode,
  holidayBank = [],
  selectedHolidays = [],
  formatDate,
  onClose,
  onToggleAssign,
  onAdd,
  onEdit,
  onDelete,
}) {
  const [yearFilter, setYearFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [menuId, setMenuId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const years = useMemo(() => collectHolidayYears(holidayBank), [holidayBank]);
  const selectedIds = useMemo(
    () => new Set((selectedHolidays || []).map((h) => String(h.id))),
    [selectedHolidays]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (holidayBank || []).filter((h) => {
      const y = holidayYear(h.date);
      if (yearFilter !== "all" && y !== Number(yearFilter)) return false;
      if (!q) return true;
      return (
        String(h.name || "")
          .toLowerCase()
          .includes(q) || String(h.date || "").includes(q)
      );
    });
  }, [holidayBank, yearFilter, search]);

  const byYear = useMemo(() => {
    const map = new Map();
    filtered.forEach((h) => {
      const y = holidayYear(h.date) ?? "Unknown";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(h);
    });
    for (const list of map.values()) {
      list.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    }
    return Array.from(map.entries()).sort(
      (a, b) => Number(b[0]) - Number(a[0])
    );
  }, [filtered]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[11200] flex items-center justify-center bg-black/60 p-3"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="flex h-[min(88vh,720px)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-bank-assign-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <div className="flex items-center gap-1.5">
              <h2 id="holiday-bank-assign-title" className="ds-h3">
                Holiday Bank
              </h2>
              <InfoHint title="Company holiday catalog. Tick Assign to put a holiday on this policy. Use ⋮ to edit or delete the bank entry." />
            </div>
            <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
              Year-wise list · assign to policy · manage bank
            </p>
          </div>
          <div className="flex items-center gap-2">
            <DsButton type="button" variant="primary" size="sm" onClick={onAdd}>
              <DsIcon icon={Plus} size="sm" />
              Add holiday
            </DsButton>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <DsIcon icon={X} size="md" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-2">
          <div className="relative min-w-[12rem] flex-1">
            <DsIcon
              icon={Search}
              size="sm"
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
            />
            <DsInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              darkMode={darkMode}
              className="rounded-xl pl-8"
            />
          </div>
          <button
            type="button"
            onClick={() => setYearFilter("all")}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              yearFilter === "all"
                ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                : "bg-[hsl(var(--ds-muted))]"
            }`}
          >
            All years
          </button>
          {years.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setYearFilter(String(y))}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                String(yearFilter) === String(y)
                  ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                  : "bg-[hsl(var(--ds-muted))]"
              }`}
            >
              {y}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-3">
          {byYear.length === 0 ? (
            <p className="ds-caption p-8 text-center text-[hsl(var(--ds-muted-fg))]">
              No holidays in bank. Click Add holiday.
            </p>
          ) : (
            byYear.map(([year, list]) => (
              <div key={String(year)} className="mb-4">
                <div className="mb-1.5 flex items-center justify-between px-1">
                  <h3 className="text-sm font-semibold">{year}</h3>
                  <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                    {list.length}
                  </span>
                </div>
                <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))]">
                  <table className="ds-table min-w-full text-sm">
                    <thead className="ds-table-thead bg-[hsl(var(--ds-muted))]">
                      <tr>
                        <th className="w-16 px-3 py-2 text-left">Assign</th>
                        <th className="px-3 py-2 text-left">Holiday</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="w-14 px-3 py-2 text-right"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((holiday) => {
                        const assigned = selectedIds.has(String(holiday.id));
                        return (
                          <tr
                            key={holiday.id}
                            className="border-t border-[hsl(var(--ds-border))]"
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={assigned}
                                onChange={() => onToggleAssign?.(holiday)}
                                className="rounded border-[hsl(var(--ds-border))] text-[hsl(var(--ds-primary))]"
                                title={
                                  assigned
                                    ? "Remove from this policy"
                                    : "Add to this policy"
                                }
                              />
                            </td>
                            <td className="px-3 py-2 font-medium">
                              {holiday.name}
                            </td>
                            <td className="px-3 py-2">
                              {formatDate(holiday.date)}
                            </td>
                            <td className="px-3 py-2">
                              <DsBadge tone={holidayTypeTone(holiday.type)}>
                                {formatHolidayTypeLabel(holiday.type)}
                              </DsBadge>
                            </td>
                            <td className="relative px-3 py-2 text-right">
                              <DsButton
                                type="button"
                                variant="outline"
                                size="sm"
                                className="!h-8 !w-8 !px-0"
                                onClick={() =>
                                  setMenuId((v) =>
                                    v === holiday.id ? null : holiday.id
                                  )
                                }
                              >
                                <DsIcon icon={MoreVertical} size="sm" />
                              </DsButton>
                              {menuId === holiday.id ? (
                                <div className="absolute right-3 z-30 mt-1 w-32 overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
                                  <button
                                    type="button"
                                    className="block w-full px-3 py-2 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
                                    onClick={() => {
                                      setMenuId(null);
                                      onEdit?.(holiday);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="block w-full px-3 py-2 text-left text-xs text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                                    onClick={() => {
                                      setMenuId(null);
                                      setConfirmDeleteId(holiday.id);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="primary" size="sm" onClick={onClose}>
            Done
          </DsButton>
        </div>
      </div>

      {confirmDeleteId != null
        ? createPortal(
            <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-sm rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-[var(--ds-shadow-card)]">
                <h3 className="ds-h4 mb-2">Delete holiday?</h3>
                <p className="ds-caption mb-4 text-[hsl(var(--ds-muted-fg))]">
                  Removes it from the company bank and from this policy if
                  selected.
                </p>
                <div className="flex justify-end gap-2">
                  <DsButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDeleteId(null)}
                  >
                    Cancel
                  </DsButton>
                  <DsButton
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      const id = confirmDeleteId;
                      setConfirmDeleteId(null);
                      onDelete?.(id);
                    }}
                  >
                    Delete
                  </DsButton>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>,
    document.body
  );
}
